import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Plus, Trash2, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  const token = () => localStorage.getItem('vendor_token');
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => { fetchMembers(); }, []);

  const fetchMembers = async () => {
    try {
      const { data } = await axios.get(`${API}/vendor/team`, auth());
      setMembers(data.members || []);
    } catch {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/vendor/team`, form, auth());
      toast.success('Team member added');
      setForm({ name: '', email: '', password: '' });
      setDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add member');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${API}/vendor/team/${m.id}`, { is_active: !m.is_active }, auth());
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_active: !x.is_active } : x)));
      toast.success(!m.is_active ? 'Access enabled' : 'Access disabled');
    } catch {
      toast.error('Could not update member');
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name}? They will no longer be able to sign in.`)) return;
    try {
      await axios.delete(`${API}/vendor/team/${m.id}`, auth());
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      toast.success('Team member removed');
    } catch {
      toast.error('Could not remove member');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Team</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Add staff who can manage <strong>Orders</strong> and <strong>Products</strong> only. Every action is logged in Activity.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-member-btn"><Plus className="h-4 w-4 mr-2" /> Add team member</Button>
          </DialogTrigger>
          <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl">
            <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
            <form onSubmit={addMember} className="space-y-4">
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="member-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="member-email" />
              </div>
              <div className="space-y-2">
                <Label>Temporary password</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="member-password" />
                <p className="text-xs text-muted-foreground">Share these with your staff — they sign in at the same login page.</p>
              </div>
              <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Team members can view &amp; manage orders and products. They cannot change store settings, theme, subscription, or manage the team.
              </div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="save-member">
                {saving ? 'Adding…' : 'Add member'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No team members yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add staff to help manage orders and products.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {members.map((m) => (
            <Card key={m.id} className={m.is_active ? '' : 'opacity-60'}>
              <CardContent className="flex flex-col sm:flex-row sm:items-center gap-3 py-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{m.name}</p>
                    <Badge variant="outline" className="text-[10px]">Staff</Badge>
                    {!m.is_active && <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-600">Disabled</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5 break-all">
                    <Mail className="h-3 w-3" /> {m.email}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Can manage: Orders &amp; Products</p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <span className="text-muted-foreground">{m.is_active ? 'Active' : 'Off'}</span>
                    <Switch checked={m.is_active} onCheckedChange={() => toggleActive(m)} data-testid={`toggle-member-${m.id}`} />
                  </label>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeMember(m)} data-testid={`remove-member-${m.id}`} aria-label="Remove">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamMembers;
